//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import {
  IMPLEMENTED_SURFACE_FORMS, PANEL_ANCHORS, PANEL_TITLE_MAX_LENGTH,
  PLUGIN_SURFACE_FORMS, type ActivePlugin, type ImplementedSurfaceForm,
  type PanelAnchor, type PluginSurface, type PluginSurfaceForm,
  type PluginSurfaceKind
} from '@/services/apiTypes';
import { pluginSurfacesQuery } from '@/services/pluginQueries';
import { isSameOriginUrl } from '@/utils/SameOrigin';

// Every rejection names the plugin and surface key, because a manifest is
// authored in another repo: the reader of this message is usually not the
// person who can see the offending file.
const where = (plugin: string, key: string): string => `${plugin}.${key}`;

// Sizes are CSS pixels the geometry module arithmetics on, so anything that
// is not a positive finite number would propagate as NaN through a clamp and
// place the panel nowhere in particular.
const isPositiveFinite = (value: unknown): boolean =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const isPanelAnchor = (value: unknown): value is PanelAnchor =>
  PANEL_ANCHORS.includes(value as PanelAnchor);

// Which kinds each implemented form can render. `json` + `panel` is the one
// empty cell: there is no json panel body to render a `PluginInfoPayload`
// into, and rejecting the pairing beats leaving the surface as a modal,
// which would look to its author exactly like the field being ignored.
const FORM_KINDS: Record<
  ImplementedSurfaceForm, readonly PluginSurfaceKind[]
> = {
  modal: ['json', 'webview'],
  panel: ['webview']
};

const isSurfaceForm = (value: unknown): value is PluginSurfaceForm =>
  PLUGIN_SURFACE_FORMS.includes(value as PluginSurfaceForm);

const isImplementedForm = (
  form: PluginSurfaceForm
): form is ImplementedSurfaceForm =>
  (IMPLEMENTED_SURFACE_FORMS as readonly string[]).includes(form);

/**
 * Resolve and check a surface's declared form, returning the form in effect.
 *
 * A name outside the universe and a name inside it but unbuilt get different
 * messages on purpose: the first is a typo to fix, the second a capability to
 * wait for, and a manifest author reading the message cannot see this repo to
 * work out which they hit.
 */
const validateForm = (
  plugin: string, key: string, kind: PluginSurfaceKind, declared: unknown
): ImplementedSurfaceForm => {
  if (declared === undefined) return 'modal';
  if (!isSurfaceForm(declared)) {
    throw new Error(
      `[plugins] unknown surface form on ${where(plugin, key)}: ` +
      `${JSON.stringify(declared)}. Expected one of ` +
      `${PLUGIN_SURFACE_FORMS.join(', ')}, of which ` +
      `${IMPLEMENTED_SURFACE_FORMS.join(', ')} are implemented.`
    );
  }
  if (!isImplementedForm(declared)) {
    throw new Error(
      `[plugins] surface form "${declared}" is reserved but not implemented ` +
      `by this host, and ${where(plugin, key)} declares it. Implemented ` +
      `forms: ${IMPLEMENTED_SURFACE_FORMS.join(', ')}.`
    );
  }
  if (!FORM_KINDS[declared].includes(kind)) {
    throw new Error(
      `[plugins] form "${declared}" is not supported for ${kind} surfaces, ` +
      `and ${where(plugin, key)} declares it. Build a ${kind} ${declared} ` +
      'body before a manifest declares one.'
    );
  }
  return declared;
};

/**
 * Validate the `panel` block that a surface declaring `form: 'panel'` must
 * carry.  Throws on any violation, matching how the surrounding collector
 * treats an unknown `kind` or an off-origin URL: at v0.0.x of this contract a
 * malformed block is a bug between this repo and the backend, and a silent
 * skip would open the surface as a modal with no indication that its author
 * asked for a panel.
 *
 * `icon` is deliberately not checked against the host's allowlist -- an
 * unrecognized name resolves to the fallback glyph by design, and duplicating
 * the vocabulary as a validation rule would break every manifest naming an
 * icon added later.
 */
const validatePanel = (
  plugin: string, key: string, panel: unknown
): void => {
  if (
    typeof panel !== 'object' || panel === null || Array.isArray(panel)
  ) {
    throw new Error(
      `[plugins] panel must be an object on ${where(plugin, key)}: ` +
      `${String(panel)}.`
    );
  }
  const spec = panel as Record<string, unknown>;

  // Trimmed, because a title of spaces produces a pill with no visible
  // identity at all -- worse than a missing one, which at least throws.
  const title = spec.title;
  if (
    typeof title !== 'string' || title.trim() === '' ||
    title.length > PANEL_TITLE_MAX_LENGTH
  ) {
    throw new Error(
      '[plugins] panel title must be a non-empty string of at most ' +
      `${PANEL_TITLE_MAX_LENGTH} characters on ${where(plugin, key)}: ` +
      `${JSON.stringify(title)}.`
    );
  }

  if (!isPanelAnchor(spec.anchor)) {
    throw new Error(
      `[plugins] panel anchor must be one of ${PANEL_ANCHORS.join(', ')} ` +
      `on ${where(plugin, key)}: ${JSON.stringify(spec.anchor)}.`
    );
  }

  for (const field of ['width', 'height'] as const) {
    if (!isPositiveFinite(spec[field])) {
      throw new Error(
        `[plugins] panel ${field} must be a positive finite number on ` +
        `${where(plugin, key)}: ${JSON.stringify(spec[field])}.`
      );
    }
  }

  for (const field of ['min_width', 'min_height'] as const) {
    if (spec[field] !== undefined && !isPositiveFinite(spec[field])) {
      throw new Error(
        `[plugins] panel ${field} must be a positive finite number when ` +
        `present on ${where(plugin, key)}: ${JSON.stringify(spec[field])}.`
      );
    }
  }

  // Each of these defaults when absent, so a truthy non-boolean would be
  // read as the opposite of what a manifest author wrote -- the same
  // inversion hazard the backend loader rejects `default_visible` for.
  for (const field of ['resizable', 'closable', 'preload'] as const) {
    if (spec[field] !== undefined && typeof spec[field] !== 'boolean') {
      throw new Error(
        `[plugins] panel ${field} must be a boolean when present on ` +
        `${where(plugin, key)}: ${JSON.stringify(spec[field])}.`
      );
    }
  }

  if (spec.icon !== undefined && typeof spec.icon !== 'string') {
    throw new Error(
      '[plugins] panel icon must be a string when present on ' +
      `${where(plugin, key)}: ${JSON.stringify(spec.icon)}.`
    );
  }
};

// One row in the gear dropdown's Plugins section.  Each row carries the
// surface and the manifest key so we can render multiple surfaces of the
// same kind on a single plugin (e.g. `info_main` + `info_compliance`).
// Dispatch at render time on `surface.kind`, not on `key` — the key is a
// free-form identifier with no contract.
export interface PluginSurfaceRow {
  plugin: ActivePlugin;
  surface: PluginSurface;
  key: string;
}

/**
 * Validate and flatten every plugin's declared surfaces into a single row
 * list.  Throws on contract violations (unknown surface `kind`, malformed
 * or off-origin `url`, a `form` that is unknown, unimplemented, or unusable
 * for the kind, and a `panel` block that is missing, malformed, or declared
 * without `form: 'panel'`) rather than silently skipping them — at this stage
 * of the plugin feature, divergence between the frontend and backend repos
 * is a bug that should surface immediately, not be papered over with
 * forward-compat tolerance.  The two off-origin defenses are independent:
 * the kind check guards the dispatch; the URL check guards the iframe and
 * fetch boundaries against a compromised or misconfigured manifest.
 */
export const collectPluginSurfaceRows = (
  plugins: ActivePlugin[]
): PluginSurfaceRow[] => {
  const rows: PluginSurfaceRow[] = [];
  for (const plugin of plugins) {
    for (const [key, raw] of Object.entries(plugin.surfaces ?? {})) {
      const kind = (raw as { kind?: unknown }).kind;
      if (kind !== 'json' && kind !== 'webview') {
        throw new Error(
          '[plugins] unsupported surface kind on ' +
          `${plugin.name}.${key}: ${String(kind)}. ` +
          'Add a renderer arm in the dispatch and a case in this validator ' +
          'before the backend ships a new kind.'
        );
      }
      const url = (raw as { url?: unknown }).url;
      // Reject anything that does not resolve to this application's own
      // origin — an off-host document would otherwise be loaded into the
      // iframe or fetched from. Absolute as well, since a relative path
      // would resolve against whatever route the menu happened to be opened
      // from. See `isSameOriginUrl` for why the origin is parsed rather than
      // spelled out as a prefix test.
      if (
        typeof url !== 'string' ||
        !url.startsWith('/') ||
        !isSameOriginUrl(url)
      ) {
        throw new Error(
          '[plugins] surface URL must be a same-origin absolute path on ' +
          `${plugin.name}.${key}: ${String(url)}. ` +
          'Off-origin, protocol-relative, or malformed URLs are rejected ' +
          'as defense-in-depth against a compromised manifest.'
        );
      }
      const declaredForm = (raw as { form?: unknown }).form;
      const form = validateForm(plugin.name, key, kind, declaredForm);
      const panel = (raw as { panel?: unknown }).panel;
      if (form === 'panel') {
        if (panel === undefined) {
          throw new Error(
            '[plugins] form "panel" needs a panel block on ' +
            `${where(plugin.name, key)}: the block carries the title and ` +
            'size the panel cannot open without.'
          );
        }
        validatePanel(plugin.name, key, panel);
      } else if (panel !== undefined) {
        // An absent form resolves to modal, so naming the resolved form
        // would send the reader hunting for a line the manifest never had.
        const declares =
          declaredForm === undefined ? 'no form' : `"${form}"`;
        throw new Error(
          '[plugins] a panel block needs form "panel" on ' +
          `${where(plugin.name, key)}, which declares ${declares}. Honoring ` +
          'it anyway would make presence-of-block a second, contradictory ' +
          'way to choose a form.'
        );
      }
      rows.push({ plugin, surface: raw as PluginSurface, key });
    }
  }
  return rows;
};

/**
 * The surfaces query with validation folded into its `select`, which is the
 * one place every consumer can share it.
 *
 * The distinction matters for where a rejection lands.  Called from a
 * component's render, the collector's throws escape into whatever tree that
 * component sits in -- and the header sits inside the route tree, where a bad
 * manifest would replace every authenticated route with the root error page.
 * Thrown from `select`, the same rejection becomes this query's error
 * instead: the header quietly draws no plugin entries, and the panel clients
 * -- which sit inside PanelErrorBoundary, the surface meant to pay for a bad
 * manifest -- rethrow it there, where it is shown loudly without costing the
 * application.
 */
export const pluginSurfaceRowsQuery = () => ({
  ...pluginSurfacesQuery(),
  select: collectPluginSurfaceRows
});
